import debug from 'debug';
import {
    fromEither,
    fromNullable,
    fromPredicate,
    none,
    Option,
    some,
} from 'fp-ts/lib/Option';
import { Coordinate } from 'ol/coordinate';
import * as format from 'ol/format';
import { get } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import Projection from 'ol/proj/Projection';
import proj4 from 'proj4';
import { getDataCrs, getMapCrs, parseCRSName } from '../app/queries';
import { fst, iife, Pair, pair, snd } from '../lib';
import { AppConfig, CRSConfig, DirectGeometryObject } from '../source';
import { filterNotNull, noop, once } from '../util';
import {
    FeaturePathIO,
    MainLayerCollection,
    type Coord2D,
    type FeaturePath,
    type IMapViewData,
    type IMark,
    type Interaction,
    type InteractionLabel,
    type NonNullFeaturePath,
    type PrintRequest,
    type PrintResponse,
    type Scale,
    type ZoomLevel,
} from './types';
import { Extent } from 'ol/extent';
import LayerGroup from 'ol/layer/Group';
import SourceVector from 'ol/source/Vector';

const logger = debug('sdi:map/functions');

export const formatGeoJSON = new format.GeoJSON();

// // this definition used to be distributed by proj4, and is wrong
// // proj4.defs(
// //     'EPSG:31370',
// //     '+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.8686,52.2978,-103.7239,-0.3366,0.457,-1.8422,-1.2747 +units=m +no_defs'
// // );

// proj4.defs(
//     'EPSG:31370',
//     '+proj=lcc +lat_0=90 +lon_0=4.36748666666667 +lat_1=51.1666672333333 +lat_2=49.8333339 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.8686,52.2978,-103.7239,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs +type=crs',
// );

export const setupProj = once(() => {
    const dataCRS = getDataCrs();
    const mapCRS = getMapCrs();
    if (typeof dataCRS !== 'string') {
        proj4.defs(dataCRS.name, dataCRS.defs);
    }
    if (typeof mapCRS !== 'string') {
        proj4.defs(mapCRS.name, mapCRS.defs);
    }

    register(proj4 as any); // This have to be after proj4.defs -
});

const getProjection = (getter: () => CRSConfig, dflt: Projection) => () => {
    logger('getProjection', getter === undefined);
    const crs = getter();
    const prj = iife(() => {
        if (typeof crs === 'string') {
            return get(crs);
        }
        return get(crs.name);
    });
    return prj ?? dflt;
};

export const dataProjection = getProjection(
    getDataCrs,
    new Projection({ code: 'EPSG:4326' }),
);

export const mapProjection = getProjection(
    getMapCrs,
    new Projection({ code: 'EPSG:3857' }),
);

const getStringValue = (input: string | (() => string)) =>
    typeof input === 'string' ? input : input();

export const proj4Transformer = (source: string, target: string) =>
    proj4(source, target).forward;

export const geometryTransformer =
    (source: string | (() => string), target: string | (() => string)) =>
    (geom: DirectGeometryObject) => {
        const forward = proj4Transformer(
            getStringValue(source),
            getStringValue(target),
        );
        switch (geom.type) {
            case 'Point':
                return {
                    ...geom,
                    coordinates: forward(geom.coordinates),
                };
            case 'LineString':
                return {
                    ...geom,
                    coordinates: geom.coordinates.map(c => forward(c)),
                };
            case 'Polygon':
                return {
                    ...geom,
                    coordinates: geom.coordinates.map(ring =>
                        ring.map(c => forward(c)),
                    ),
                };
            case 'MultiPoint':
                return {
                    ...geom,
                    coordinates: geom.coordinates.map(c => forward(c)),
                };
            case 'MultiLineString':
                return {
                    ...geom,
                    coordinates: geom.coordinates.map(line =>
                        line.map(c => forward(c)),
                    ),
                };
            case 'MultiPolygon':
                return {
                    ...geom,
                    coordinates: geom.coordinates.map(poly =>
                        poly.map(ring => ring.map(c => forward(c))),
                    ),
                };
        }
    };

export const toLonLat = geometryTransformer(
    () => mapProjection().getCode(),
    'EPSG:4326',
);

export const toMapProj = geometryTransformer(
    () => dataProjection().getCode(),
    () => mapProjection().getCode(),
);

export const lonlatToMapProjection = (lon: number, lat: number) => {
    const forward = proj4Transformer('EPSG:4326', mapProjection().getCode());
    return forward([lon, lat]) as Coord2D;
};
export const lonlatTo4326 = (lon: number, lat: number) => {
    const forward = proj4Transformer(mapProjection().getCode(), 'EPSG:4326');
    return forward([lon, lat]) as Coord2D;
};

export const tryCoord2D = (c: Coordinate | undefined): Option<Coord2D> => {
    if (c !== undefined && c.length > 1) {
        return some([c[0], c[1]]);
    }
    return none;
};

export const defaultMark = (): IMark => ({
    started: false,
    endTime: 0,
    coordinates: [0, 0],
});

export const getFeaturePath = ({ layerId, featureId }: FeaturePath) => {
    if (layerId !== null && featureId !== null) {
        return some({ layerId, featureId } as NonNullFeaturePath);
    }
    return none;
};

export const nullFeaturePath = (): FeaturePath => ({
    layerId: null,
    featureId: null,
});

export const defaultMapView = (config: AppConfig): IMapViewData => ({
    dirty: 'geo',
    srs: parseCRSName(config.mapCrs),
    center: [148885, 170690],
    rotation: 0,
    zoom: 8,
    feature: null,
    extent: null,
});

export const defaultInteraction = (): Interaction => ({
    label: 'select',
    state: { selected: null },
});

export const singleClickInteraction = (): Interaction => ({
    label: 'singleclick',
    state: null,
});

/**
 *
 * withInteraction('select', (i) => do(i), () => doNothing());
 *
 */
export const withInteraction =
    <T extends Interaction>(
        label: InteractionLabel,
        w: (i: T) => void,
        wo = noop,
    ) =>
    (i: Interaction | Readonly<Interaction>) => {
        switch (i.label) {
            case 'select':
                label === 'select' ? w(i as T) : wo();
                break;
            case 'enter':
                label === 'enter' ? w(i as T) : wo();
                break;
            case 'create':
                label === 'create' ? w(i as T) : wo();
                break;
            case 'modify':
                label === 'modify' ? w(i as T) : wo();
                break;
            case 'track':
                label === 'track' ? w(i as T) : wo();
                break;
            case 'measure':
                label === 'measure' ? w(i as T) : wo();
                break;
            case 'extract':
                label === 'extract' ? w(i as T) : wo();
                break;
            case 'mark':
                label === 'mark' ? w(i as T) : wo();
                break;
            case 'print':
                label === 'print' ? w(i as T) : wo();
                break;
            case 'position':
                label === 'position' ? w(i as T) : wo();
                break;
            case 'singleclick':
                label === 'singleclick' ? w(i as T) : wo();
                break;
        }
    };

export const withInteractionOpt =
    <T extends Interaction, R = unknown>(
        label: InteractionLabel,
        w: (i: T | Readonly<T>) => R,
    ) =>
    (i: Interaction | Readonly<Interaction>) => {
        switch (i.label) {
            case 'select':
                return label === 'select' ? some(w(i as T)) : none;
            case 'enter':
                return label === 'enter' ? some(w(i as T)) : none;
            case 'create':
                return label === 'create' ? some(w(i as T)) : none;
            case 'modify':
                return label === 'modify' ? some(w(i as T)) : none;
            case 'track':
                return label === 'track' ? some(w(i as T)) : none;
            case 'measure':
                return label === 'measure' ? some(w(i as T)) : none;
            case 'extract':
                return label === 'extract' ? some(w(i as T)) : none;
            case 'mark':
                return label === 'mark' ? some(w(i as T)) : none;
            case 'print':
                return label === 'print' ? some(w(i as T)) : none;
            case 'position':
                return label === 'position' ? some(w(i as T)) : none;
            case 'singleclick':
                return label === 'singleclick' ? some(w(i as T)) : none;
        }
    };

export const defaultPrintRequest = (): PrintRequest<null> => ({
    id: null,
    width: 0,
    height: 0,
    resolution: 0,
    props: null,
});

export const defaultPrintResponse = (): PrintResponse<null> => ({
    id: null,
    data: '',
    extent: [0, 0, 0, 0],
    status: 'none',
    props: null,
});

// round to 2 significant digit
const roundTo2 = (num: number) => {
    const significant_nb = -Math.floor(Math.log10(Math.abs(num))) + 1;
    const d = Math.pow(10, significant_nb);
    return Math.round(num * d) / d;
};

/*
 * scale: denominator of the ratio (ex: '10 000' if scale is '1:10 000')
 * calculations come from https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
 * In Lambert 72, the first tile (at zoom zéro) is 268.40708 km in a tile of 256px ->  1048.4652 m/px
 * 39.37 is the number of inches per meters
 * ppi set at 96 px/in
 */
// const scaleToZoom = (scale: number) => {
//     const pixelRatio = window.devicePixelRatio;
//     const ppi = 96 * pixelRatio;
//     return Math.log2(1048.4652 * ppi * 39.37 / scale);
// }
export const zoomToScale = (z: number) => {
    // const z = parseInt(zoom);
    // const pixelRatio = window.devicePixelRatio;
    const pixelRatio = 1;
    const ppi = 96 * pixelRatio;
    const scale = (ppi * 39.37 * 1048.4652) / Math.pow(2, z);
    return roundTo2(scale);
};
// export const calcAllScales = () =>
//     Object.keys(Svalues).map(z => zoomToScale(z))

const zoomLevelList = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30,
];

export const scaleList = [
    4500000, 2000000, 1000000, 550000, 250000, 150000, 70000, 35000, 20000,
    8500, 4500, 2000, 1000, 550, 300, 150, 65, 35, 15, 8, 4, 2, 1, 0.5, 0.3,
    0.1, 0.05, 0.03, 0.02, 0.008, 0.004,
];
export const reasonableScaleList = scaleList.slice(4, 16);

export const tryZoom = (zoom: number) =>
    zoomLevelList.indexOf(zoom) >= 0 ? some(zoom as ZoomLevel) : none;
export const tryScale = (scale: number) =>
    scaleList.indexOf(scale) >= 0 ? some(scale as Scale) : none;

const ScaleLambert72Values: Pair<ZoomLevel, Scale>[] = [
    pair(0, 4500000),
    pair(1, 2000000),
    pair(2, 1000000),
    pair(3, 550000),
    pair(4, 250000),
    pair(5, 150000),
    pair(6, 70000),
    pair(7, 35000),
    pair(8, 20000),
    pair(9, 8500),
    pair(10, 4500),
    pair(11, 2000),
    pair(12, 1000),
    pair(13, 550),
    pair(14, 300),
    pair(15, 150),
    pair(16, 65),
    pair(17, 35),
    pair(18, 15),
    pair(19, 8),
    pair(20, 4),
    pair(21, 2),
    pair(22, 1),
    pair(23, 0.5),
    pair(24, 0.3),
    pair(25, 0.1),
    pair(26, 0.05),
    pair(27, 0.03),
    pair(28, 0.02),
    pair(29, 0.008),
    pair(30, 0.004),
];

export const zoomToRoundScale = (z: ZoomLevel) => {
    const rs = ScaleLambert72Values.find(p => fst(p) === z);
    if (rs === undefined) {
        throw new Error("Zoom don't match with a scale");
    }
    return snd(rs);
};
export const scaleToZoom = (s: Scale) => {
    const z = ScaleLambert72Values.find(p => snd(p) === s);
    if (z === undefined) {
        throw new Error("Scale don't match with a zoom");
    }
    return fst(z);
};

// export const minScale = () => snd(ScaleLambert72Values[0]);
// export const maxScale = () => snd(ScaleLambert72Values[ScaleLambert72Values.length - 1]);

export const AboutToRefreshEvent = 'about-to-refresh';

export const serializeFeaturePath = (fp: NonNullFeaturePath) =>
    JSON.stringify(fp);
export const deserializeFeaturePath = (fps: string) => {
    try {
        return fromEither(FeaturePathIO.decode(JSON.parse(fps)));
    } catch (_err) {
        return none;
    }
};

export const validExtent = fromPredicate<Extent>(extent =>
    extent.every(n => !Number.isNaN(n)),
);

// this isn’t as brazen as it looks, we make sure earlier that we set a source on each layer in the group
export const getGroupSources = (group: LayerGroup): SourceVector[] =>
    filterNotNull(
        group
            .getLayersArray()
            .map(layer => layer.getSource() as SourceVector | null),
    );

export const getGroups = (main: MainLayerCollection) =>
    main.getArray().filter(candidate => candidate instanceof LayerGroup);

export const getGroupById = (main: MainLayerCollection, id: string) =>
    fromNullable(getGroups(main).find(group => group.get('id') === id));

export const findFeatureInCollection = (
    main: MainLayerCollection,
    layerId: string,
    featureId: number | string,
) =>
    getGroupById(main, layerId).chain(group => {
        for (const source of getGroupSources(group)) {
            const candidate = source.getFeatureById(featureId);
            if (candidate !== null) {
                return some(candidate);
            }
        }
        return none;
    });

export const findLayerFeatureInCollection = (
    main: MainLayerCollection,
    layerId: string,
    featureId: number | string,
) =>
    getGroupById(main, layerId).chain(group => {
        for (const layer of group.getLayersArray()) {
            const source = layer.getSource() as SourceVector | null;
            if (source === null) {
                continue;
            }
            const feature = source.getFeatureById(featureId);
            if (feature !== null) {
                return some({ layer, feature });
            }
        }
        return none;
    });

export const selectExtentInCollection = (
    main: MainLayerCollection,
    extent: Extent,
) =>
    main
        .getArray()
        .filter(candidate => candidate instanceof LayerGroup)
        .map(group => ({
            lid: group.get('id') as string,
            fs: getGroupSources(group)
                .map(source => source.getFeaturesInExtent(extent))
                .flat(),
        }));

logger('loaded');
